import React from 'react';
import { Link } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { MemberDetails } from '~/models/MemberDetails';
import { useIsLargeScreen } from '~/hooks/useIsLargeScreen';
import { HStack } from '~/components/gluestack/hstack';
import { VStack } from '~/components/gluestack/vstack';
import { Text } from '~/components/gluestack/text';
import { RenderHtml } from '~/components/ui/RenderHtml';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { ProfileSection } from './ProfileSection';
import { ProfilePhotosPreview } from './ProfilePhotosPreview';
import { PROFILE_PHOTOS_PREVIEW_MAX_COUNT } from '@/hooks/useMemberPreviewPhotos';
import { MemberListCard } from '@/components/domain/common/MemberListCard';
import { useCommunity } from '~/hooks/useCommunity';
import { Link04Icon } from '@hugeicons-pro/core-stroke-rounded';

function WebsiteUrlDisplay({ url }: { url: string }) {
  const scheme = url.startsWith('https://') ? 'https://' : 'http://';
  const display = url.replace(/^https?:\/\//, '');
  const slashIndex = display.indexOf('/');
  const domain = slashIndex === -1 ? display : display.slice(0, slashIndex);
  const path = slashIndex === -1 ? '' : display.slice(slashIndex);
  return (
    <>
      <Text className="text-primary-500">{scheme}</Text>
      <Text bold className="text-primary-500">{domain}</Text>
      {path ? <Text className="text-primary-500">{path}</Text> : null}
    </>
  );
}

interface ProfileTabAboutEventProps {
  member: MemberDetails;
  onPhotosTabPress?: () => void;
}

export function ProfileTabAboutEvent({
  member,
  onPhotosTabPress,
}: ProfileTabAboutEventProps) {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const community = useCommunity();
  const communityName = community?.name || '';
  const eventDetails = member?.EventDetails;

  const characterLimit = isLargeScreen ? 1800 : 600;

  return (
    <VStack className="gap-3 px-3 md:px-4">
      <ProfilePhotosPreview
        memberId={member.Id}
        publicPhotosCount={member.PublicPhotosCount ?? 0}
        profilePhotoId={member.PhotoId}
        profilePhotoUrl={member.PhotoUrl}
        maxCount={PROFILE_PHOTOS_PREVIEW_MAX_COUNT}
        onSeeAllPress={onPhotosTabPress}
        showLoadingSkeleton={(member.PublicPhotosCount ?? 0) >= 2}
        skeletonCount={(member.PublicPhotosCount ?? 1) - 1}
        size={isLargeScreen ? 'lg' : 'sm'}
      />

      {member.Description ? (
        <ProfileSection className="pt-2 md:pt-1">
          <VStack className="gap-0">
            <RenderHtml
              html={member.Description}
              characterLimit={characterLimit}
              detectHttpsLinks
              detectHashtags
              noTruncate={member.IsAdmin}
            />
          </VStack>
        </ProfileSection>
      ) : null}

      {eventDetails?.Website ? (
        <ProfileSection title={t('events.website')}>
          <HStack className="items-center gap-2">
            <HugeIconsWrapper icon={Link04Icon} size={16} className="text-typography-500" />
            <Link href={eventDetails.Website as any} target="_blank" className="text-primary-500 no-underline text-base active:opacity-70 web:cursor-pointer web:hover:underline" numberOfLines={1}>
              <WebsiteUrlDisplay url={eventDetails.Website} />
            </Link>
          </HStack>
        </ProfileSection>
      ) : null}

      {eventDetails?.CreatedBy ? (
        <ProfileSection
          title={
            eventDetails.CreatorIsOrganizer
              ? t('events.organizer')
              : t('events.curator')
          }>
          <VStack className="gap-2">
            <MemberListCard member={eventDetails.CreatedBy} actionMode="none" />
            {!eventDetails.CreatorIsOrganizer ? (
              <Text size="sm" className="leading-snug text-typography-500">
                {t('events.curatorNotice', { communityName })}
              </Text>
            ) : null}
          </VStack>
        </ProfileSection>
      ) : null}
    </VStack>
  );
}
